
import React from 'react';
import { Card } from "@/components/ui/card";
import { Users, TrendingUp, Medal } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, ReferenceLine } from 'recharts';
import type { PerformanceData, PeerComparisonData } from './types';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';

interface ComparativePeerAnalyticsProps {
  performanceData: PerformanceData[];
}

export const ComparativePeerAnalytics: React.FC<ComparativePeerAnalyticsProps> = ({ performanceData }) => {
  // Fetch peer data for comparison
  const { data: peerData, isLoading } = useQuery({
    queryKey: ['peer_performance_data'],
    queryFn: async () => {
      try {
        // In a real implementation, you would fetch actual peer data
        // Here we'll generate synthetic peer data based on the user's performance
        
        // First, get the user's subjects
        const userSubjects = [...new Set(performanceData.map(data => data.subject))];
        
        // Generate peer comparison data for each subject
        return await Promise.all(userSubjects.map(async (subject) => {
          // Get user's average score for this subject
          const userPerformance = performanceData
            .filter(data => data.subject === subject);
            
          const userScore = userPerformance.length > 0
            ? userPerformance.reduce((sum, item) => sum + item.score, 0) / userPerformance.length
            : 0;
          
          // Generate synthetic peer data
          // In a real implementation, this would come from the database
          const peerMin = Math.max(20, Math.round(userScore * 0.7));
          const peerMax = Math.min(100, Math.round(userScore * 1.3));
          const peerMedian = Math.round((peerMin + peerMax) / 2);
          const peerAverage = Math.round(peerMedian * 0.95);
          const peerQuartile1 = Math.round((peerMin + peerMedian) / 2);
          const peerQuartile3 = Math.round((peerMedian + peerMax) / 2);
          
          // Calculate percentile (synthetic)
          // In a real implementation, this would be based on actual distribution
          const percentile = Math.min(99, Math.max(1, 
            userScore > peerMedian 
              ? 50 + Math.round((userScore - peerMedian) / (peerMax - peerMedian) * 50)
              : Math.round((userScore - peerMin) / (peerMedian - peerMin) * 50)
          ));
          
          return {
            subject,
            userScore,
            peerAverage,
            percentile,
            peerMin,
            peerMax,
            peerMedian,
            peerQuartile1,
            peerQuartile3
          };
        }));
      } catch (err) {
        console.error('Error fetching peer data:', err);
        return [];
      }
    },
    enabled: performanceData.length > 0
  });
  
  // Transform data for the box plot visualization
  const chartData = React.useMemo(() => {
    if (!peerData) return [];
    
    return peerData.map(data => ({
      subject: data.subject,
      min: data.peerMin,
      q1: data.peerQuartile1,
      median: data.peerMedian,
      q3: data.peerQuartile3,
      max: data.peerMax,
      avg: data.peerAverage,
      user: data.userScore,
    }));
  }, [peerData]);
  
  const getPercentileColor = (percentile: number) => {
    if (percentile >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (percentile >= 70) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (percentile >= 50) return 'text-purple-600 bg-purple-50 border-purple-200';
    if (percentile >= 30) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  if (isLoading || !performanceData.length) {
    return (
      <Card className="p-6 bg-gradient-to-br from-indigo-50 to-white border border-slate-100 shadow-sm">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-500">
          <Users className="w-5 h-5 text-purple-500" />
          Comparative Peer Analytics
        </h2>
        <div className="h-[300px] flex items-center justify-center">
          <p className="text-gray-500">
            {isLoading ? 'Loading peer comparison data...' : 'Not enough data for peer comparison.'}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-indigo-50 to-white border border-slate-100 shadow-sm">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-500">
        <Users className="w-5 h-5 text-purple-500" />
        Comparative Peer Analytics
      </h2>
      
      <div className="flex flex-col space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {peerData?.slice(0, 3).map((data) => (
            <div key={data.subject} className="p-3 rounded-lg border bg-white flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-700">{data.subject}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-500">Your score:</span>
                  <Badge variant="outline" className="text-xs font-bold">
                    {data.userScore.toFixed(1)}%
                  </Badge>
                </div>
              </div>
              <div className="text-right">
                <div className={`px-3 py-1.5 rounded-full border ${getPercentileColor(data.percentile)}`}>
                  <div className="flex items-center gap-1.5">
                    <Medal className="w-3.5 h-3.5" />
                    <span className="text-xs font-bold">{data.percentile}th</span>
                  </div>
                  <div className="text-[10px]">percentile</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis 
                dataKey="subject" 
                tick={{ fill: '#6B7280', fontSize: 12 }}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fill: '#6B7280' }}
                axisLine={{ stroke: '#E5E7EB' }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                formatter={(value: number, name: string) => {
                  switch (name) {
                    case 'min': return [`${value}%`, 'Min Peer Score'];
                    case 'q1': return [`${value}%`, '25th Percentile'];
                    case 'median': return [`${value}%`, 'Median Peer Score'];
                    case 'q3': return [`${value}%`, '75th Percentile'];
                    case 'max': return [`${value}%`, 'Max Peer Score'];
                    case 'avg': return [`${value}%`, 'Avg Peer Score'];
                    case 'user': return [`${value}%`, 'Your Score'];
                    default: return [value, name];
                  }
                }}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  borderRadius: '8px', 
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  border: '1px solid #E5E7EB'
                }}
              />
              <Legend />
              
              {/* Box plot (custom implementation) */}
              <Bar
                dataKey="q3"
                stackId="boxplot"
                fill="#94A3B8"
                opacity={0.7}
                name="75th Percentile"
              />
              <Bar
                dataKey="median"
                stackId="boxplot"
                fill="#64748B"
                opacity={0.8}
                name="Median Peer Score"
              />
              <Bar
                dataKey="q1"
                stackId="boxplot"
                fill="#94A3B8"
                opacity={0.7}
                name="25th Percentile"
              />
              
              {/* min-max */}
              <Bar
                dataKey="min"
                fill="#CBD5E1"
                opacity={0.5}
                name="Min Peer Score"
              />
              <Bar
                dataKey="max"
                fill="#CBD5E1"
                opacity={0.5}
                name="Max Peer Score"
              />
              
              {/* User's score */}
              <Line
                type="monotone"
                dataKey="user"
                stroke="#8B5CF6"
                strokeWidth={2}
                dot={{ fill: '#8B5CF6', r: 5 }}
                name="Your Score"
              />
              
              {/* Average peer score */}
              <Line
                type="monotone"
                dataKey="avg"
                stroke="#06B6D4"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#06B6D4', r: 4 }}
                name="Avg Peer Score"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        
        <div className="mt-2 text-xs text-gray-600">
          <p className="font-medium">How to read this chart:</p>
          <ul className="mt-1 list-disc list-inside space-y-1">
            <li>The box represents the middle 50% of peer scores (25th to 75th percentile)</li>
            <li>The solid line in the box is the median peer score</li>
            <li>The dashed blue line shows the average peer score</li>
            <li>The purple line with dots shows your performance</li>
            <li>Higher percentiles indicate better standing compared to peers</li>
          </ul>
        </div>
      </div>
    </Card>
  );
};
